import { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Copy, Check, Loader2, ExternalLink, Trash2, AlertTriangle, UserPlus, Download, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${t}` } };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function DistrictAdminInviteModal({ onClose }) {
  const [tab, setTab] = useState("invite"); // 'invite' | 'links'
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Principal");
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(90);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null); // 'preview' | <token> | null

  const downloadBlobPdf = async (url, filename, opts = {}) => {
    const res = await axios.get(url, { responseType: "blob", ...opts });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const dlUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(dlUrl);
  };

  const handleDownloadEducatorPdf = async () => {
    setDownloadingPdf("preview");
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadBlobPdf(
        `${API}/educator/district-admin/pdf?days=7`,
        `District_Insights_Preview_${today}.pdf`,
        authHeaders()
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Could not download the PDF preview.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadInvitePdf = async (token) => {
    setDownloadingPdf(token);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadBlobPdf(
        `${API}/public/district-admin/${token}/pdf`,
        `District_Insights_${today}.pdf`
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Could not download the PDF for this invite.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const loadInvites = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/educator/district-admin/invites`, authHeaders());
      setInvites(res.data || []);
    } catch {
      setInvites([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (tab === "links") loadInvites();
  }, [tab]);

  const handleInvite = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/educator/district-admin/invite`,
        {
          to_email: email.trim() || null,
          role_title: role.trim() || "Administrator",
          label: label.trim() || null,
          expires_in_days: days,
          days_window: 7,
        },
        authHeaders()
      );
      setResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create invite");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Revoke this district link? Anyone with the URL will lose access.")) return;
    try {
      await axios.post(`${API}/educator/district-admin/invite/${token}/revoke`, {}, authHeaders());
      loadInvites();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not revoke");
    }
  };

  const flagOff = result && result.email_enabled === false && result.delivery;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="district-admin-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-500 text-white p-5 sm:p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20"
            data-testid="district-admin-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
            District Co-Pilot
          </div>
          <h2 className="text-2xl font-extrabold mt-1 flex items-center gap-2">
            <UserPlus className="w-6 h-6" /> Invite District Admin
          </h2>
          <p className="text-sm opacity-95 mt-1">
            Give your principal, superintendent, or workforce partner read-only access to a
            weekly rollup across all your classes.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-5 sm:px-6 flex gap-1">
          {[
            { k: "invite", label: "New invite" },
            { k: "links", label: "Active invites" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2.5 text-sm font-semibold border-b-2 ${
                tab === t.k
                  ? "border-purple-500 text-purple-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`district-admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {tab === "invite" ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1.5">
                  Recipient email <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="principal@school.edu"
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="district-admin-email-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank to just create a copyable link.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-1.5">Role title</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm"
                    data-testid="district-admin-role-select"
                  >
                    <option>Principal</option>
                    <option>Superintendent</option>
                    <option>District Coordinator</option>
                    <option>Workforce Partner</option>
                    <option>Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-1.5">Expires in</label>
                  <div className="flex bg-white border border-slate-300 rounded-xl overflow-hidden">
                    {[30, 60, 90].map((d) => (
                      <button
                        key={d}
                        onClick={() => setDays(d)}
                        className={`flex-1 px-3 py-2 text-sm font-semibold ${
                          days === d
                            ? "bg-purple-500 text-white"
                            : "text-slate-600 hover:bg-purple-50"
                        }`}
                        data-testid={`district-admin-days-${d}`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1.5">
                  Label <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Spring 2026 progress check"
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="district-admin-label-input"
                />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={handleInvite}
                  disabled={sending}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white font-bold rounded-xl px-5 py-2.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="district-admin-create-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Creating…" : email.trim() ? "Send invite" : "Create link"}
                </button>
                <button
                  onClick={handleDownloadEducatorPdf}
                  disabled={downloadingPdf === "preview"}
                  className="inline-flex items-center gap-2 bg-white border-2 border-purple-300 text-purple-700 font-bold rounded-xl px-4 py-2.5 hover:bg-purple-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="district-admin-preview-pdf-btn"
                  title="Download a printable PDF of your full district rollup right now"
                >
                  {downloadingPdf === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {downloadingPdf === "preview" ? "Preparing…" : "Preview PDF"}
                </button>
              </div>

              {result && (
                <div
                  className={`rounded-xl border p-4 mt-2 ${
                    flagOff
                      ? "bg-amber-50 border-amber-200"
                      : "bg-emerald-50 border-emerald-200"
                  }`}
                  data-testid="district-admin-result"
                >
                  {flagOff && (
                    <div className="flex gap-2 items-start text-sm text-amber-900 mb-3">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold">Email delivery is off.</div>
                        <p className="text-xs">
                          Copy the link below. It'll auto-send once SendGrid + the{" "}
                          <code className="bg-amber-100 px-1 rounded">CLASS_INSIGHTS_EMAIL_ENABLED</code>{" "}
                          flag are on.
                        </p>
                      </div>
                    </div>
                  )}
                  {result.share_url && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <input
                        readOnly
                        value={result.share_url}
                        onClick={(e) => e.target.select()}
                        className="flex-1 min-w-[220px] bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono"
                        data-testid="district-admin-share-url"
                      />
                      <button
                        onClick={() => copyLink(result.share_url, "result")}
                        className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-semibold rounded-lg px-3 py-1.5 hover:bg-slate-700"
                        data-testid="district-admin-copy-btn"
                      >
                        {copied === "result" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied === "result" ? "Copied" : "Copy"}
                      </button>
                      <a
                        href={result.share_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open
                      </a>
                      {result.token && (
                        <button
                          onClick={() => handleDownloadInvitePdf(result.token)}
                          disabled={downloadingPdf === result.token}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900 disabled:opacity-60"
                          data-testid="district-admin-result-pdf-btn"
                        >
                          {downloadingPdf === result.token ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          PDF
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              {loadingList ? (
                <div className="flex justify-center py-8 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : invites.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No invites yet.</p>
              ) : (
                <ul className="space-y-2">
                  {invites.map((inv) => {
                    const url = `${window.location.origin}/district/${inv.token}`;
                    const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                    const inactive = inv.revoked || expired;
                    return (
                      <li
                        key={inv.token}
                        className={`border rounded-xl p-3 ${
                          inactive ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"
                        }`}
                        data-testid={`district-admin-row-${inv.token.slice(0, 6)}`}
                      >
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 text-sm truncate">
                              {inv.role_title} · {inv.to_email || "(link only)"}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {inv.label ? `${inv.label} · ` : ""}
                              {inv.view_count || 0} views ·{" "}
                              {inv.revoked ? (
                                <span className="text-rose-600 font-semibold">REVOKED</span>
                              ) : expired ? (
                                <span className="text-amber-600 font-semibold">EXPIRED</span>
                              ) : (
                                <>Valid through {formatDate(inv.expires_at)}</>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              disabled={inactive}
                              onClick={() => copyLink(url, inv.token)}
                              className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 hover:bg-slate-100 disabled:opacity-40"
                            >
                              {copied === inv.token ? (
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                              {copied === inv.token ? "Copied" : "Copy"}
                            </button>
                            <button
                              disabled={inactive || downloadingPdf === inv.token}
                              onClick={() => handleDownloadInvitePdf(inv.token)}
                              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 ${
                                inactive
                                  ? "hidden"
                                  : "text-purple-700 hover:bg-purple-50"
                              }`}
                              data-testid={`district-admin-row-pdf-${inv.token.slice(0, 6)}`}
                              title="Download the printable PDF version of this district rollup"
                            >
                              {downloadingPdf === inv.token ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Download className="w-3.5 h-3.5" />
                              )}
                              PDF
                            </button>
                            {!inactive && (
                              <button
                                onClick={() => revokeInvite(inv.token)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 rounded-lg px-2 py-1.5 hover:bg-rose-50"
                                data-testid={`district-admin-revoke-${inv.token.slice(0, 6)}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Revoke
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
